import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import { ProjectTagActionsMenu, ProjectTagsTable } from '@trinity/chat-components/components/ProjectTags';
import {
  TagStateContext,
  useTagStateReducer,
  buildProjectTagsWithChildren,
} from '@trinity/chat-components/contexts/project-tags';
import { TabViewTableSkeleton } from '@/components/TabView';
import { ProjectDetailsContext, ProjectStateLoadingContext } from '@/screens/Project.Main/Context';
import type { TagItem } from '@trinity/chat-components/contexts/project-tags';
import { useModal } from '@trinity/chat-components/components/Modal/index.js';
import styles from './style/Project.Tags.module.css';
import { TagDetails } from './Tag.Details';
import * as Modal from './Modal';
import * as Table from '@trinity/chat-components/components/Table/index.js';
import { useCreateTagMutation } from './hooks/useCreateTagMutation';
import { useDeleteTagMutation } from './hooks/useDeleteTagMutation';
import { useUpdateProjectTagMutation } from './hooks/useUpdateProjectTagMutation';
import { useReorderTagsMutation } from './hooks/useReorderTagsMutation';
import { useToggleTagVisibilityMutation } from './hooks/useToggleTagVisibilityMutation';
import Button from '@trinity/chat-components/components/presentation/Button/Button.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';

export const ProjectTags = () => {
  const loading = useContext(ProjectStateLoadingContext);
  const projectDetails = useContext(ProjectDetailsContext);
  const [state, dispatch] = useTagStateReducer();

  const projectId = projectDetails.query.data?.project.id;
  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: ['get:projects/tags', projectId],
    queryFn: async () => apiClient.projects.fetchProjectTags({ projectId }),
    enabled: !!projectId && !loading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: (res) => {
      return { tags: buildProjectTagsWithChildren(res.tags) };
    },
  });

  const tags = query.data?.tags || [];

  const params = useParams<{ tagId?: string }>();
  const tagId = params.tagId ? parseInt(params.tagId, 10) : null;
  const isViewingTagDetails = !!tagId;

  const selectedTag = useMemo(() => {
    if (!tagId) return null;
    const findTag = (
      list: (ProjectConferenceTagWithMetadata | TagItem)[]
    ): ProjectConferenceTagWithMetadata | null => {
      for (const tag of list) {
        if (tag.id === tagId) return tag;
        if ('children' in tag && tag.children?.length > 0) {
          const found = findTag(tag.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findTag(tags);
  }, [tagId, tags]);

  useEffect(() => {
    dispatch({ type: 'initialize', state: { tags } });
  }, [dispatch, tags]);

  if (loading || query.isLoading) {
    return <TabViewTableSkeleton />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <TagStateContext.Provider value={{ dispatch, projectId: projectId ?? 0, state }}>
          {isViewingTagDetails ? (
            <TagDetails tag={selectedTag} />
          ) : (
            <ProjectTagsContent projectId={projectId} />
          )}
        </TagStateContext.Provider>
      </div>
    </div>
  );
};

type ProjectTagsContentProps = {
  projectId: number;
};

const ProjectTagsContent = ({ projectId }: ProjectTagsContentProps) => {
  const createMutation = useCreateTagMutation(projectId);
  const updateMutation = useUpdateProjectTagMutation(projectId);
  const deleteMutation = useDeleteTagMutation(projectId);
  const reorderMutation = useReorderTagsMutation(projectId);
  const toggleVisibilityMutation = useToggleTagVisibilityMutation(projectId);

  const [createParentTagId, setCreateParentTagId] = useState<number | null>(null);
  const [activeHasChildren, setActiveHasChildren] = useState(false);
  const [activeTag, setActiveTag] = useState<ProjectConferenceTagWithMetadata | null>(null);

  const [toggleCreateModal, CreateTagModal] = useModal(Modal.CreateTag, { keepMounted: false });
  const [toggleEdit, EditModal] = useModal(Modal.EditTag, { keepMounted: false });
  const [toggleDelete, DeleteModal] = Modal.useDeleteTagModal({
    onDelete: () => {
      if (activeTag) {
        deleteMutation.mutate({ tagId: activeTag.id, projectId }, { onSuccess: toggleDelete });
      }
    },
  });

  const renderRowActions = useCallback(
    (tag: ProjectConferenceTagWithMetadata, close: () => void, hasChildren: boolean) => (
      <ProjectTagActionsMenu
        tag={tag}
        close={close}
        onEdit={() => {
          setActiveTag(tag);
          setActiveHasChildren(hasChildren);
          toggleEdit();
        }}
        onCreateSubtag={() => {
          setCreateParentTagId(tag.id);
          toggleCreateModal();
        }}
        onDelete={() => {
          setActiveTag(tag);
          toggleDelete();
        }}
        onToggleHide={() =>
          toggleVisibilityMutation.mutate({
            tagId: tag.id,
            hidden: !tag.hidden,
            parentId: tag.parent?.id ?? null,
          })
        }
        disableDelete={hasChildren}
      />
    ),
    [toggleDelete, toggleEdit, toggleVisibilityMutation]
  );

  return (
    <>
      <Table.Layout.Box>
        <Table.Layout.Header title="Tags">
          <div className={styles.actions}>
            <TagCreationButton
              onClickCreateTag={() => {
                setCreateParentTagId(null);
                toggleCreateModal();
              }}
            />
          </div>
        </Table.Layout.Header>
        <ProjectTagsTable
          getTagDetailUrl={(id) => `/projects/${projectId}/tags/${id}`}
          canManageTags
          onReorder={(ordinalMap) => reorderMutation.mutate(ordinalMap)}
          onUpdateTag={(p) => updateMutation.mutate({ ...p, parentId: null })}
          renderRowActions={renderRowActions}
        />
      </Table.Layout.Box>
      <CreateTagModal
        parentTagId={createParentTagId}
        onSave={(params) => createMutation.mutateAsync(params)}
        isPending={createMutation.isPending}
      />
      {activeTag && (
        <EditModal
          tag={activeTag}
          hasChildren={activeHasChildren}
          onSave={(params) =>
            updateMutation.mutateAsync({ ...params, tagId: activeTag.id, parentId: params.parentId ?? 0 })
          }
          isPending={updateMutation.isPending}
        />
      )}
      <DeleteModal />
    </>
  );
};

type TagCreationButtonProps = {
  onClickCreateTag: () => void;
};

const TagCreationButton = (props: TagCreationButtonProps) => {
  const appThemingContext = useContext(AppThemingContext);

  return (
    <Button
      className={styles.btn}
      color={
        appThemingContext?.enabled && appThemingContext?.palette?.primary?.main ? undefined : 'secondary'
      }
      onClick={props.onClickCreateTag}
      variant="brick"
      style={
        appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
          ? ({
              borderColor: appThemingContext.palette.primary.main,
              color: appThemingContext.palette.primary.main,
              backgroundColor: 'transparent',
              '--theme-color': appThemingContext.palette.primary.main,
            } as React.CSSProperties)
          : undefined
      }
    >
      New
    </Button>
  );
};
